import { createQueryOptions, useQuery } from '@connectrpc/connect-query'
import { getSystemSettings } from '../gen/cortado/v1/cortado-CortadoService_connectquery'
import { create } from '@bufbuild/protobuf'
import { GetSystemSettingsRequestSchema } from '../gen/cortado/v1/settings_pb'
import { useAuth } from '../hooks/useAuth'
import type { Transport } from '@connectrpc/connect'
import { connectTransport } from '@/lib/connect-transport'
import { useStore } from '@tanstack/react-store'
import { organizationStore } from './organization.store'

/**
 * Query options for the system settings of a specific organization.
 *
 * `organizationId` is required: `Settings.eventTypes` is org-scoped data, and
 * the backend rejects the request with InvalidArgument when a user belongs to
 * more than one organization and omits it.
 */
export function getSystemSettingsQueryOptions(
  organizationId: string,
  transport: Transport = connectTransport,
) {
  return {
    ...createQueryOptions(
      getSystemSettings,
      create(GetSystemSettingsRequestSchema, { organizationId }),
      { transport },
    ),
    staleTime: 24 * 60 * 60 * 1000,
    retry: 2,
  }
}

/**
 * Hook to fetch system settings (all enum values)
 *
 * This hook fetches all system enum values that can be used
 * for dropdowns, filters, and validation throughout the app.
 *
 * Settings include:
 * - Inquiry stages
 * - Booking stages
 * - Event stages
 * - Quote statuses
 * - Payment statuses
 * - Item units
 * - Event types (per-organization data, not a proto enum)
 * - Parent types
 * - Discount types
 * - User scheduling statuses
 * - Task statuses
 * - Organization roles
 * - Organization features
 * - System roles
 * - System permissions
 *
 * Settings are prefetched when the app initializes (in root-provider.tsx)
 * and cached for 24 hours as they rarely change. This means in most cases,
 * the settings will be available immediately without a loading state.
 *
 * Only fetches settings when the user is authenticated and an organization is
 * selected. The selected organization ID is part of the request (and therefore
 * of the query key), so switching orgs fetches that org's settings rather than
 * serving the previous org's cached event types.
 *
 * @returns Query result with settings data, loading state, and error
 */
export function useSystemSettings() {
  const { session, isLoading: isAuthLoading } = useAuth()
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId,
  )

  const query = useQuery(
    getSystemSettings,
    create(GetSystemSettingsRequestSchema, {
      organizationId: selectedOrganizationId || '',
    }),
    {
      staleTime: 24 * 60 * 60 * 1000, // 24 hours - settings rarely change
      retry: 2, // Retry twice on failure
      // Only fetch when authenticated AND an org is selected - a multi-org user
      // gets InvalidArgument from the backend without an organization_id.
      enabled: !!session && !isAuthLoading && !!selectedOrganizationId,
    }
  )

  return {
    settings: query.data?.settings,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
